// KAN-729 — memory-only stale-while-revalidate cache for platform views (dashboard
// reads, company Recent activity). Entries are keyed `${userId}:${aal}|${scope}`, so
// one session's data is never read under another user or another assurance level
// (a pre-2FA aal1 read is EMPTY under RLS). Never persisted; cleared at sign-out
// (clearPerUserDeviceState + clearSignedOutMemoryState).
import { supabase } from '../../supabase';
import { readSessionAal } from '../auth/readSessionAal';
import { decodeAalFromJwt } from '../auth/aalClaim';

const entries = new Map(); // `${sessionKey}|${scope}` -> value (insertion order = recency)
let sessionKey = null; // last VERIFIED `${userId}:${aal}`; null = unknown -> no cache
let epoch = 0; // bumped by clear: a read started before sign-out cannot repopulate

/** Verifies who is signed in, at which aal, right now. Unknown -> null (callers must not cache). */
export async function resolveViewSessionKey(client = supabase) {
  const startedAt = epoch;
  const s = await readSessionAal(client);
  if (startedAt !== epoch) return null;
  sessionKey = s ? `${s.userId}:${s.aal}` : null;
  return sessionKey;
}

/** Sync: the last verified session key (what a first render may seed from). */
export const peekViewSessionKey = () => sessionKey;

export function readViewCache(key, scope) {
  if (!key) return undefined;
  const k = `${key}|${scope}`;
  if (!entries.has(k)) return undefined;
  const v = entries.get(k);
  entries.delete(k); entries.set(k, v); // refresh recency
  return v;
}

/**
 * Writes under a verified key; `cap` keeps only the newest N entries of this scope's prefix.
 * Refuses any key that is not the CURRENT session key, so a read or write that resolves
 * after sign-out (or after an aal1 -> aal2 step-up) can never re-seed the old session's data.
 */
export function writeViewCache(key, scope, value, { cap = 50, prefix = scope } = {}) {
  if (!key || key !== sessionKey) return;
  entries.delete(`${key}|${scope}`);
  entries.set(`${key}|${scope}`, value);
  const own = [...entries.keys()].filter((k) => k.startsWith(`${key}|${prefix}`));
  own.slice(0, Math.max(0, own.length - cap)).forEach((k) => entries.delete(k));
}

export function currentViewEpoch() { return epoch; }

export function clearPlatformViewCache() {
  epoch += 1;
  entries.clear();
  sessionKey = null;
}

/**
 * The session key follows the LIVE session: any auth event whose user or aal differs
 * from the verified key (2FA step-up, a different account, sign-out) drops it, so a
 * first render never seeds from a key the session no longer holds (KAN-675 pattern).
 */
export function syncViewSessionKeyFromSession(session) {
  const userId = session?.user?.id;
  const aal = decodeAalFromJwt(session?.access_token);
  const live = userId && (aal === 'aal1' || aal === 'aal2') ? `${userId}:${aal}` : null;
  if (live !== sessionKey) sessionKey = null;
}

if (supabase?.auth?.onAuthStateChange) {
  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_OUT') { clearPlatformViewCache(); return; }
    syncViewSessionKeyFromSession(session);
  });
}
